import React, { useEffect, useState } from "react";
import { Alert, Button, Select, Spin } from "antd";
import { DownloadOutlined } from "@ant-design/icons";
import { Document, Page, pdfjs } from "react-pdf";
import {
  SourceDocument,
  sourceDocumentsService as service,
} from "./sourceDocuments.service";
import { pdfWorkerSrc } from "../../utils/pdfWorker";

pdfjs.GlobalWorkerOptions.workerSrc = pdfWorkerSrc;

const pdfOptions = { isEvalSupported: false };

export default function OriginalDocument({
  estateId,
  document,
}: {
  estateId: string;
  document: SourceDocument;
}) {
  const [page, setPage] = useState(1);
  const [pages, setPages] = useState(0);
  const [zoom, setZoom] = useState(1);
  const panel = React.useRef<HTMLDivElement>(null);
  const [width, setWidth] = useState(400);
  useEffect(() => {
    if (!panel.current) return;
    const observer = new ResizeObserver((entries) =>
      setWidth(Math.max(100, entries[0].contentRect.width))
    );
    observer.observe(panel.current);
    return () => observer.disconnect();
  }, []);
  const [url, setUrl] = useState<string>();
  const [error, setError] = useState<string>();
  const pdf = /\.pdf$/i.test(document.filename);
  const image = /\.(png|jpe?g|webp)$/i.test(document.filename);
  useEffect(() => {
    let live = true;
    let objectUrl: string | undefined;
    setUrl(undefined);
    setError(undefined);
    setPage(1);
    setPages(0);
    setZoom(1);
    if (!pdf && !image) return;
    service
      .original(estateId, document.uid)
      .then((blob) => {
        if (!live) return;
        const ext = document.filename.split(".").pop()?.toLowerCase();
        const type = pdf
          ? "application/pdf"
          : `image/${ext === "jpg" || ext === "jpeg" ? "jpeg" : ext}`;
        objectUrl = URL.createObjectURL(new Blob([blob], { type }));
        setUrl(objectUrl);
      })
      .catch(() => {
        if (live)
          setError("Could not open the original. You can download it instead.");
      });
    return () => {
      live = false;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [estateId, document.uid, document.filename, pdf, image]);
  return (
    <div className="sd-original" ref={panel}>
      <div className="sd-original-toolbar">
        <span>Original document</span>
        <Button
          size="small"
          icon={<DownloadOutlined />}
          onClick={() =>
            service
              .download(estateId, document)
              .catch(() => setError("Download failed. Please retry."))
          }
        >
          Download
        </Button>
      </div>
      {error ? (
        <Alert type="error" message={error} />
      ) : !pdf && !image ? (
        <Alert
          type="info"
          message="Open this file in its original application"
          description="Download Word documents and spreadsheets to inspect their formatting. Extracted information is available alongside this panel."
        />
      ) : !url ? (
        <div className="sd-preview-loading">
          <Spin />
          <span>Opening original document</span>
        </div>
      ) : pdf ? (
        <>
          <div className="sd-pdf-navigation">
            <Button
              size="small"
              disabled={page <= 1}
              onClick={() => setPage((old) => old - 1)}
            >
              Previous page
            </Button>
            <span>
              Page {page} of {pages || "..."}
            </span>
            <Button
              size="small"
              disabled={page >= pages}
              onClick={() => setPage((old) => old + 1)}
            >
              Next page
            </Button>
            <Select
              aria-label="PDF zoom"
              size="small"
              value={zoom}
              onChange={setZoom}
              options={[
                { value: 1, label: "Fit width" },
                { value: 1.5, label: "150%" },
                { value: 2, label: "200%" },
              ]}
            />
          </div>
          <div className="sd-pdf-pages">
            <Document
              file={url}
              options={pdfOptions}
              onLoadSuccess={({ numPages }) => setPages(numPages)}
              onLoadError={() =>
                setError(
                  "Could not display this PDF. You can download the original instead."
                )
              }
              loading={<Spin />}
            >
              <Page
                pageNumber={page}
                width={(width - 2) * zoom}
                renderAnnotationLayer={false}
                renderTextLayer={false}
                loading={<Spin />}
              />
            </Document>
          </div>
        </>
      ) : (
        <img src={url} alt="Original uploaded document" />
      )}
    </div>
  );
}
